/**
 * Social Post Status Constants
 * 
 * Defines the post lifecycle statuses and their display properties
 */

export const POST_STATUS = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  PROCESSING: 'PROCESSING',
  POSTED: 'POSTED',
  ERROR: 'ERROR'
} as const;

export type PostStatus = typeof POST_STATUS[keyof typeof POST_STATUS];

export const STATUS_LABELS: Record<PostStatus, string> = {
  [POST_STATUS.DRAFT]: 'Draft',
  [POST_STATUS.SCHEDULED]: 'Scheduled',
  [POST_STATUS.PROCESSING]: 'Publishing...',
  [POST_STATUS.POSTED]: 'Published',
  [POST_STATUS.ERROR]: 'Failed'
};

export const STATUS_COLORS: Record<PostStatus, string> = {
  [POST_STATUS.DRAFT]: 'bg-gray-500',
  [POST_STATUS.SCHEDULED]: 'bg-blue-500',
  [POST_STATUS.PROCESSING]: 'bg-yellow-500',
  [POST_STATUS.POSTED]: 'bg-green-500',
  [POST_STATUS.ERROR]: 'bg-red-500'
};

export const STATUS_ICONS = {
  CLOCK: 'Clock',
  SPINNER: 'Loader2',
  CHECK: 'CheckCircle2',
  ALERT: 'AlertCircle',
  SEND: 'Send'
} as const;

/**
 * Get available actions for a post based on its status
 */
export function getAvailableActions(status: PostStatus): {
  canEdit: boolean;
  canDelete: boolean;
  canSchedule: boolean;
  canPublishNow: boolean;
  canRetry: boolean;
  canViewOnPlatform: boolean;
} {
  switch (status) {
    case POST_STATUS.DRAFT:
      return {
        canEdit: true,
        canDelete: true,
        canSchedule: true,
        canPublishNow: true,
        canRetry: false,
        canViewOnPlatform: false
      };
    
    case POST_STATUS.SCHEDULED:
      return {
        canEdit: true,
        canDelete: true,
        canSchedule: false,
        canPublishNow: true, // Override schedule
        canRetry: false,
        canViewOnPlatform: false
      };
    
    case POST_STATUS.PROCESSING:
      return {
        canEdit: false,
        canDelete: false,
        canSchedule: false,
        canPublishNow: false,
        canRetry: false,
        canViewOnPlatform: false
      };
    
    case POST_STATUS.POSTED:
      return {
        canEdit: false,
        canDelete: true,
        canSchedule: false,
        canPublishNow: false,
        canRetry: false,
        canViewOnPlatform: true
      };
    
    case POST_STATUS.ERROR:
      return {
        canEdit: true,
        canDelete: true,
        canSchedule: true,
        canPublishNow: true,
        canRetry: true,
        canViewOnPlatform: false
      };
    
    default:
      return {
        canEdit: false,
        canDelete: false,
        canSchedule: false,
        canPublishNow: false,
        canRetry: false,
        canViewOnPlatform: false
      };
  }
}

/**
 * Get time remaining for a scheduled post
 */
export function getTimeRemaining(scheduledTime: string | null): string | null {
  if (!scheduledTime) return null;
  
  const scheduled = new Date(scheduledTime);
  const now = new Date();
  
  if (scheduled <= now) {
    return 'Past due';
  }
  
  const diff = scheduled.getTime() - now.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} remaining`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
  } else {
    return 'Less than a minute';
  }
}